"use client";

import Image from "next/image";
import type { CSSProperties } from "react";

import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { formatCopy } from "@/lib/copy-format";
import { getCommonCopy } from "@/lib/common-copy";
import logoImageSources from "@/data/hero-logo-sources.generated.json";

const LOGO_IMAGE_SIZES = "(max-width: 809px) 92px, (max-width: 1439px) 108px, 115px";
const logoSources: Record<string, { avif: string; webp: string }> = logoImageSources;

type HeroLogoImage = {
  alt: string;
  href: string;
  slug?: string;
  src: string;
};

type HeroLogoText = {
  alt: string;
  brand: "mercer";
  href: string;
  text: string;
};

type HeroLogo = HeroLogoImage | HeroLogoText;

const HERO_LOGO_ENTER_BASE_DELAY_S = 0;
const HERO_LOGO_ENTER_STAGGER_S = 0.05;

function isTextLogo(logo: HeroLogo): logo is HeroLogoText {
  return "text" in logo;
}

function getLogoKey(logo: HeroLogo, index: number) {
  return `${isTextLogo(logo) ? logo.text : logo.src}-${index}`;
}

const HERO_LOGOS: HeroLogo[] = [
  {
    src: "/assets/imgs/hero-logos/accounting-prospects.svg",
    slug: "accounting-prospects",
    alt: "Accounting Prospects",
    href: "https://accountingprospects.com",
  },
  {
    src: "/assets/imgs/hero-logos/ignite.svg",
    alt: "Ignite Fitness",
    href: "https://ignite.fitness",
  },
  {
    src: "/assets/imgs/hero-logos/purpose-pharma.svg",
    alt: "Purpose Pharma",
    href: "https://purposepharma.com",
  },
  {
    src: "/assets/imgs/hero-logos/hawkeye.svg",
    alt: "Hawkeye Advertising",
    href: "https://hawkeyeadvertising.com.au",
  },
  {
    src: "/assets/imgs/hero-logos/lex-energy.png",
    alt: "Lex Energy",
    href: "https://lexenergy.io",
  },
  {
    src: "/assets/imgs/hero-logos/rtl-consulting.svg",
    alt: "RTL Consulting",
    href: "https://rtl-consulting.se",
  },
  {
    src: "/assets/imgs/hero-logos/northfork.png",
    alt: "Northfork",
    href: "https://northfork.ai",
  },
  {
    src: "/assets/imgs/hero-logos/climentum.svg",
    alt: "Climentum Capital",
    href: "https://climentum.com",
  },
  {
    src: "/assets/imgs/hero-logos/nordic-acceleration.png",
    alt: "Nordic Acceleration Group",
    href: "https://nordicacceleration.com",
  },
  {
    src: "/assets/imgs/hero-logos/climate-hero.svg",
    alt: "ClimateHero",
    href: "https://climatehero.me",
  },
  {
    src: "/assets/imgs/hero-logos/eodynamics.svg",
    alt: "EODynamics",
    href: "https://eodynamics.co",
  },
  {
    src: "/assets/imgs/hero-logos/lattice-international.svg",
    alt: "Lattice International",
    href: "https://lattice-international.com",
  },
  {
    src: "/assets/imgs/hero-logos/lagerblad-foods.svg",
    alt: "Lagerblad Foods Oy",
    href: "https://lagerbladfoods.fi",
  },
  {
    src: "/assets/imgs/hero-logos/host-helse.png",
    alt: "Høst Helse",
    href: "https://hosthelse.no",
  },
  {
    src: "/assets/imgs/hero-logos/auxentum.png",
    slug: "auxentum",
    alt: "Auxentum",
    href: "https://auxentum.se",
  },
  {
    src: "/assets/imgs/hero-logos/egoo-health.svg",
    alt: "egoo.health",
    href: "https://www.egoo.health",
  },
  {
    text: "Mercer Consulting, Co.",
    brand: "mercer",
    alt: "Mercer Consulting, Co.",
    href: "https://mercerconsultingco.com/",
  },
];

import type { HeroLogoBandLayout } from "@/lib/hero-theme";

type CcHeroLogoBandProps = {
  caption?: string;
  layout?: HeroLogoBandLayout;
};

export function CcHeroLogoBand({ caption, layout = "compact" }: CcHeroLogoBandProps) {
  const common = getCommonCopy(useSiteLocale());
  const logos = [...HERO_LOGOS, ...HERO_LOGOS];
  const uniqueLogoCount = HERO_LOGOS.length;

  return (
    <div
      className={`cc-hero__logo-band${layout === "full" ? " cc-hero__logo-band--full" : ""}`}
    >
      {caption ? <p className="cc-hero__logo-band-caption">{caption}</p> : null}

      <div className="cc-hero__logo-marquee">
        <ul className="cc-hero__logo-track">
          {logos.map((logo, index) => {
            const sources = isTextLogo(logo) ? undefined : logoSources[logo.src];
            const isMarqueeClone = index >= uniqueLogoCount;
            const waterfallIndex = isMarqueeClone ? null : index;
            const itemStyle =
              waterfallIndex === null
                ? undefined
                : ({
                    "--cc-hero-logo-enter-delay": `${HERO_LOGO_ENTER_BASE_DELAY_S + waterfallIndex * HERO_LOGO_ENTER_STAGGER_S}s`,
                  } as CSSProperties);

            return (
              <li
                key={getLogoKey(logo, index)}
                className={`cc-hero__logo-item${waterfallIndex === null ? " cc-hero__logo-item--instant" : ""}${isTextLogo(logo) ? " cc-hero__logo-item--text" : ""}${!isTextLogo(logo) && logo.slug ? ` cc-hero__logo-item--${logo.slug}` : ""}`}
                data-waterfall={waterfallIndex ?? undefined}
                style={itemStyle}
              >
                <a
                  className="cc-hero__logo-link"
                  href={logo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={formatCopy(common.visitSiteAria, { name: logo.alt })}
                >
                  {isTextLogo(logo) ? (
                    <span
                      className={`cc-hero__logo-text cc-hero__logo-text--${logo.brand}`}
                    >
                      {logo.text}
                    </span>
                  ) : (
                    <picture>
                      {sources?.avif ? <source type="image/avif" srcSet={sources.avif} sizes={LOGO_IMAGE_SIZES} /> : null}
                      {sources?.webp ? <source type="image/webp" srcSet={sources.webp} sizes={LOGO_IMAGE_SIZES} /> : null}
                      <Image
                        src={logo.src}
                        alt=""
                        width={338}
                        height={102}
                        className="cc-hero__logo-image"
                        sizes={LOGO_IMAGE_SIZES}
                        unoptimized={Boolean(sources?.avif || sources?.webp)}
                      />
                    </picture>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
